import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import PropTypes from 'prop-types';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
  [
    'tm-button inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg',
    'border text-sm font-semibold leading-none transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-0',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'border-primary bg-primary text-primary-foreground shadow-sm hover:brightness-95 active:brightness-90',
        destructive: 'border-red-700 bg-red-600 text-white shadow-sm hover:bg-red-700 active:bg-red-800',
        outline: 'border-slate-300 bg-white text-slate-800 shadow-sm hover:border-slate-400 hover:bg-white data-[state=open]:border-slate-400 data-[state=open]:bg-white',
        secondary: 'border-slate-300 bg-slate-100 text-slate-800 hover:bg-slate-200',
        ghost: 'border-transparent bg-transparent text-slate-700 shadow-none hover:bg-slate-100 hover:text-slate-950 data-[state=open]:bg-white data-[state=open]:border-slate-300',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3 text-sm',
        lg: 'h-11 rounded-lg px-6',
        icon: 'h-10 w-10 p-0',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  }
);

export const Button = React.forwardRef(({
  className, variant, size, asChild, ...props
}, ref) => {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({ variant, size }), className)} ref={ref} {...props} />;
});

Button.displayName = 'Button';
Button.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.string,
  size: PropTypes.string,
  asChild: PropTypes.bool,
};

export { buttonVariants };
